import { List } from 'immutable';
import {
    CallRecordKey,
    ChatUserKey,
    ExtensionKey,
    TicketKey,
} from './EntityKeys';
import { TypedMap } from './TypedMap';

export type EventBase = {
    timeStamp: string;
    direction?: 'OUTGOING' | 'INCOMING';
};

export type OmniCallEventKey = `omnicallevent-${string}`;
export type TicketActionEventKey = `ticketaction-${string}`;

export interface AssigneeChangedEvent extends EventBase {
    DTOSubtype: 'OmniAssigneeChangedEvent';
    key: TicketActionEventKey;
    prevValue?: ExtensionKey;
    newValue?: ExtensionKey;
    actor: ExtensionKey | 'SYSTEM';
}

export interface StateChangedEvent extends EventBase {
    DTOSubtype: 'OmniStateChangedEvent';
    key: TicketActionEventKey;
    prevValue?: string;
    newValue: string;
    actor: ExtensionKey | 'SYSTEM';
}

export interface SubjectChangedEvent extends EventBase {
    DTOSubtype: 'OmniSubjectChangedEvent';
    key: TicketActionEventKey;
    prevValue?: string;
    newValue: string;
    actor: ExtensionKey | 'SYSTEM';
}
/**
 * Wire DTOSubtype of the consolidated call event — `OmniCallEventDTO.DTO_SUBTYPE` in
 * api-internal. Note it is NOT derived from the class name the way most are (cf.
 * `OmniEmailMessageDTO` → `OmniEmailMessageTicketEvent`), so it has to be read from the DTO.
 * Imported wherever the subtype is matched, so the string lives in exactly one place.
 */
export const OMNI_CALL_EVENT_DTO_SUBTYPE = 'OmniCallEvent';

/**
 * Consolidated call event (events version 3+). The API sends the call's full `CallRecord` as
 * `callRecord`; on ingest that record is upserted into the canonical call-records store and the
 * stored event is stripped to just its `callRecordKey`, so the card renders from the call-records
 * store by key (one source of truth, no duplicated/drift-prone copy here). This describes the
 * STORED shape — hence `callRecordKey` rather than the incoming `callRecord`.
 */
export interface CallRecordTicketEvent extends EventBase {
    DTOSubtype: typeof OMNI_CALL_EVENT_DTO_SUBTYPE;
    key: OmniCallEventKey;
    callRecordKey: CallRecordKey;
}

export type OutgoingMessageStatus =
    | 'DELETED'
    | 'DELIVERED'
    | 'FAILED'
    | 'READ'
    | 'SENDING'
    | 'SENT';

export interface SenderData {
    sender: ExtensionKey | string;
    senderType: Sender;
    name?: string;
}

export type Sender = 'ACCOUNT' | 'EXTERNAL' | 'SYSTEM';

export type AttachmentKey = `attachment-${string}`;
export interface TicketAttachment {
    key: AttachmentKey;
    fileName: string;
    fileSize: number;
    fileType: string;
    url: string;
    previewUrl?: string;
    previewWidth?: number;
    previewHeight?: number;
    aspectRatio?: number;
    path?: string;
    contentId?: string; // At the time of writing relevant in email attachments only
}

export interface TicketAttachmentPlaceholder {
    path?: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    aspectRatio?: number;
}

export type MessageBase = EventBase & {
    message?: string;
    key: ConversationMessageKey;
    read: boolean; //Not yet relevant
    sender: ExtensionKey;
    senderData: TypedMap<SenderData>;
    stylings?: List<TypedMap<MessageStyling>>;
    numberOfAttachments?: number; //value from Top event so that we know we should fetch attachments
};

export type EmailRecipient = {
    name?: string;
    address: string;
};

export type EmailEventItem = MessageBase & {
    DTOSubtype: 'OmniEmailMessageTicketEvent';
    toRecipients?: List<TypedMap<EmailRecipient>>;
    ccRecipients?: List<TypedMap<EmailRecipient>>;
};
export interface InternalNote extends MessageBase {
    DTOSubtype: 'OmniInternalNoteTicketEvent';
}
export interface SmsNote extends MessageBase {
    DTOSubtype: 'OmniSmsNoteTicketEvent';
}
export interface WhatsAppMessage extends MessageBase {
    DTOSubtype: 'OmniWhatsAppMessageTicketEvent';
}
export interface WhatsAppTemplateMessage extends MessageBase {
    DTOSubtype: 'OmniWhatsAppTemplateMessageTicketEvent';
}
export interface WhatsAppMessageUnsupported extends MessageBase {
    DTOSubtype: 'OmniWhatsAppUnsupportedTicketEvent';
}

interface MessageSentApiResponseBase extends EventBase {
    key: ConversationMessageKey;
    attachments?: TicketAttachment[];
    message?: string;
    sender: ExtensionKey;
    senderData: {
        sender: ExtensionKey;
        senderType: 'ACCOUNT';
    };
}

export interface EmailSentResponse extends MessageSentApiResponseBase {
    DTOSubtype: 'OmniEmailMessageTicketEvent';
}

export interface InternalNoteSentResponse extends MessageSentApiResponseBase {
    DTOSubtype: 'OmniInternalNoteTicketEvent';
    stylings?: List<TypedMap<MessageStyling>>;
}

export interface SmsSentResponse extends MessageSentApiResponseBase {
    DTOSubtype: 'OmniSmsNoteTicketEvent';
}

export interface WhatsAppMessageSentResponse extends MessageSentApiResponseBase {
    DTOSubtype: 'OmniWhatsAppMessageTicketEvent';
    status: OutgoingMessageStatus;
}

export interface WhatsAppMessageTemplateSentResponse extends MessageSentApiResponseBase {
    DOTSubtype: 'OmniWhatsAppTemplateMessageTicketEvent';
    status: OutgoingMessageStatus;
}

export type TicketEventMessageDtoSubtype =
    | 'OmniEmailMessageTicketEvent'
    | 'OmniInternalNoteTicketEvent'
    | 'OmniWhatsAppMessageTicketEvent'
    | 'OmniSmsNoteTicketEvent'
    | 'OmniWhatsAppTemplateMessageTicketEvent';

export interface PlaceholderMessage extends MessageBase {
    DTOSubtype: TicketEventMessageDtoSubtype;
    key: ConversationMessagePlaceholderKey;
}

export type Message =
    | InternalNote
    | SmsNote
    | WhatsAppMessage
    | WhatsAppTemplateMessage;

export type ConversationMessageKey =
    | `conversationmessage-${number}`
    | ConversationMessagePlaceholderKey;
export type ConversationMessagePlaceholderKey =
    `conversationmessage-placeholder-${number}`;

export const PREFIX_MESSAGE_PLACEHOLDER_KEY = 'conversationmessage-placeholder';

export interface EventDataDynamic {
    deliveryStatus: OutgoingMessageStatus;
    attachments: List<TypedMap<TicketAttachment>>;
}

export type TicketActionEvent =
    | AssigneeChangedEvent
    | StateChangedEvent
    | SubjectChangedEvent;

export type CallEvent = CallRecordTicketEvent;

export type ConversationMessageEvent =
    | EmailEventItem
    | InternalNote
    | SmsNote
    | WhatsAppMessage
    | WhatsAppTemplateMessage
    | PlaceholderMessage
    | WhatsAppMessageUnsupported;

export type TicketEventKey =
    | ConversationMessageKey
    | OmniCallEventKey
    | TicketActionEventKey;

export type TicketEvent =
    | ConversationMessageEvent
    | CallEvent
    | TicketActionEvent;

export type TicketEvents = {
    dataStatic: TypedMap<
        Record<
            TicketKey,
            TypedMap<Record<TicketEventKey, TypedMap<TicketEvent>>>
        >
    >;
    dataDynamic: TypedMap<
        Record<
            TicketKey,
            TypedMap<Record<ConversationMessageKey, TypedMap<EventDataDynamic>>>
        >
    >;
};

export const keyIsConversationMessage = (
    key: TicketEventKey,
): key is ConversationMessageKey =>
    (typeof key === 'string' && key.startsWith('conversationmessage-')) ||
    key.startsWith(PREFIX_MESSAGE_PLACEHOLDER_KEY);

export const eventIsEmail = (type: string) =>
    type === 'OmniEmailMessageTicketEvent';

export const eventIsInternalNoteOrSms = (type: string) =>
    type === 'OmniInternalNoteTicketEvent' || type === 'OmniSmsNoteTicketEvent';

export const eventIsWhatsAppMessage = (type: string) =>
    type === 'OmniWhatsAppMessageTicketEvent' ||
    type === 'OmniWhatsAppUnsupportedTicketEvent';

export const eventIsWhatsAppTemplateMessage = (type: string) =>
    type === 'OmniWhatsAppTemplateMessageTicketEvent';

export const eventIsCallRecordEvent = (type?: string) =>
    type === OMNI_CALL_EVENT_DTO_SUBTYPE;

export const eventIsTicketActionEvent = (type: string) =>
    type === 'OmniAssigneeChangedEvent' ||
    type === 'OmniStateChangedEvent' ||
    type === 'OmniSubjectChangedEvent';

export type MessageStyling = {
    startPosition: number;
    length: number;
    stylingReference: TypedMap<MessageStylingReference>;
};

export type MessageStylingReference = {
    stylingType: 'chatuser';
    chatUser?: { key: ChatUserKey | ExtensionKey };
};

export type MessageStylingType = 'chatuser';
